import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Chip,
  TextField,
  Box,
  Checkbox,
  Link,
} from "@mui/material";
import dayjs from "dayjs";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { NextAvailabilityResult } from "../../services/doctors.service";
import { PixQrModal } from "./PixQrModal";
import { ContractModal } from "./ContractModal";
import { getPriceDetails, pagarPorPix, solicitarBoleto } from "../../services/payment.service";
import { api } from "../../services/api.service";

// Roles que podem usar a opção de agendamento de teste
const TEST_ALLOWED_ROLES = ["USER","ADMIN", "OWNER", "DOCTOR_OWNER"];

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onPaid?: () => void;
  data: NextAvailabilityResult | null;
  price?: number;
}

function svgToDataUri(svg: string) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function formatRemaining(ms: number) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function PixExpirationTimer({ expiresAt }: { expiresAt: string }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const expiresMs = new Date(expiresAt).getTime();
  const remainingMs = expiresMs - Date.now();
  const expired = !expiresMs || Number.isNaN(expiresMs) || remainingMs <= 0;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        Tempo para pagar:
      </Typography>
      <Chip
        size="small"
        color={expired ? "default" : "warning"}
        label={expired ? "Expirado" : formatRemaining(remainingMs)}
      />
      <Typography variant="caption" color="text.secondary">
        (5 min)
      </Typography>
    </Stack>
  );
}

export function CheckoutModal({
  open,
  onClose,
  onPaid,
  data,
  price,
}: CheckoutModalProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useSelector((state: RootState) => state.userInfo);

  // Verifica se o usuário pode usar a opção de teste
  const canUseTestMode = TEST_ALLOWED_ROLES.includes(user?.role?.toUpperCase() || "");

  const [method, setMethod] = useState<"pix" | "card" | "boleto" | "teste">("pix");
  const [priceDetails, setPriceDetails] = useState<{
    originalAmount: number;
    cardTotalAmount: number;
    boletoTotalAmount: number;
    cardFeeAmount: number;
    boletoFeeAmount: number;
  } | null>(null);
  const [pixCopiaCola, setPixCopiaCola] = useState<string | null>(null);
  const [pixExpiresAt, setPixExpiresAt] = useState<string | null>(null);
  const [showPixDetails, setShowPixDetails] = useState(false);
  const [openPixQr, setOpenPixQr] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [openContractModal, setOpenContractModal] = useState(false);

  // Valor exibido vem do backend por método (pix/cartão/boleto), com fallback para `price`.
  const baseAmount = priceDetails?.originalAmount ?? price ?? 0;
  const amount =
    method === "card"
      ? (priceDetails?.cardTotalAmount ?? baseAmount)
      : method === "boleto"
        ? (priceDetails?.boletoTotalAmount ?? baseAmount)
        : baseAmount;
  const amountLabel = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);

  const summary = useMemo(() => {
    return {
      doctor: data?.doctorName || data?.doctorId || "-",
      local: data?.local?.localName || data?.local?.id || "-",
    };
  }, [data]);

  const elementStyle = {
      style: {
        base: {
          fontSize: "16px",
          color: "#32325d",
          "::placeholder": { color: "#a0aec0" },
        },
        invalid: { color: "#fa755a" },
      },
    };

  useEffect(() => {
    let ignore = false;
    async function loadPrice() {
      if (!open) return;
      try {
        const details = await getPriceDetails();
        if (!ignore) setPriceDetails(details);
      } catch {}
    }
    loadPrice();
    return () => {
      ignore = true;
    };
  }, [open]);

  // Ao reabrir/fechar o modal, evita mostrar um PIX antigo
  useEffect(() => {
    if (open) return;
    setPixCopiaCola(null);
    setPixExpiresAt(null);
    setShowPixDetails(false);
    setOpenPixQr(false);
    setProcessing(false);
    setContractAccepted(false);
    setOpenContractModal(false);
  }, [open]);

  const formatDateTime = (iso?: string | null) =>
    iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "-";

  const handleCopyPix = async () => {
    if (!pixCopiaCola) return;

    try {
      await navigator.clipboard.writeText(pixCopiaCola);
      toast.success("Código Pix copiado!");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const handlePay = async () => {
    // Fluxo de agendamento de teste (sem pagamento)
    if (method === "teste") {
      if (!data?.scheduleId) {
        toast.error("Dados insuficientes para criar agendamento de teste");
        return;
      }

      if (!canUseTestMode) {
        toast.error("Você não tem permissão para criar agendamentos de teste");
        return;
      }

      setProcessing(true);
      try {
        await api.post("/appointments/test", {
          scheduleId: data.scheduleId,
          patientId: user.id,
          startTime: data.slotStart || undefined,
        });

        toast.success("Agendamento de teste criado com sucesso!");
        onPaid ? onPaid() : onClose();
      } catch (e: any) {
        console.error(e);
        const msg = e?.response?.data?.message || e?.message;
        toast.error(msg ? String(msg) : "Falha ao criar agendamento de teste");
      } finally {
        setProcessing(false);
      }
      return;
    }

    if (method === "pix") {
      if (!data?.scheduleId) {
        toast.error("Dados insuficientes para gerar o Pix");
        return;
      }

      setProcessing(true);
      try {
        const pix = await pagarPorPix({
          scheduleId: data.scheduleId,
          startTime: data.slotStart || undefined,
        });

        if (!pix?.pixCopiaCola) {
          toast.error("Não foi possível gerar o Pix");
          return;
        }

        setPixCopiaCola(pix.pixCopiaCola);
        setPixExpiresAt(pix.expiresAt);
        setShowPixDetails(true);
      } catch (e: any) {
        console.error(e);
        const msg = e?.message || e?.response?.data?.message;
        toast.error(msg ? String(msg) : "Falha ao gerar Pix");
      } finally {
        setProcessing(false);
      }
      return;
    }

    if (method === "boleto") {
      if (!data?.scheduleId) {
        toast.error("Dados insuficientes para gerar o boleto");
        return;
      }

      setProcessing(true);
      try {
        const boleto = await solicitarBoleto({
          scheduleId: data.scheduleId,
          startTime: data.slotStart || undefined,
        });

        if (!boleto.hostedVoucherUrl) {
          toast.error("Não foi possível gerar o boleto");
          return;
        }

        window.open(boleto.hostedVoucherUrl, "_blank", "noopener,noreferrer");
        toast.success("Boleto gerado! Abra a nova guia para baixar o PDF.");
        onPaid ? onPaid() : onClose();
      } catch (e: any) {
        console.error(e);
        const msg = e?.message || e?.response?.data?.message;
        toast.error(msg ? String(msg) : "Falha ao gerar boleto");
      } finally {
        setProcessing(false);
      }
      return;
    }

    if (!stripe || !elements) {
      toast.error("Stripe não carregou");
      return;
    }

    if (!data?.scheduleId) {
      toast.error("Dados insuficientes para pagamento");
      return;
    }

    setProcessing(true);
    try {
      // 1️⃣ Criar PaymentIntent no backend
      // OBS: amount não vem do front; o backend calcula a partir do plano do usuário
      const { data: payment } = await api.post("/stripe/payment-intent", {
        scheduleId: data.scheduleId,
        startTime: data.slotStart || undefined,
        contractAccepted: true,
      });

      const clientSecret = payment.clientSecret;

      // 2️⃣ Confirmar pagamento
      // Como estamos usando CardNumberElement/CardExpiryElement/CardCvcElement,
      // o confirmCardPayment deve receber o CardNumberElement (não CardElement).
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error("Preencha os dados do cartão.");
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumber,
        },
      });

      if (result.error) {
        toast.error(result.error.message || "Pagamento recusado");
        return;
      }

      if (result.paymentIntent?.status === "succeeded") {
        // O agendamento será criado/confirmado pelo webhook no backend.
        toast.success("Pagamento confirmado!");
        onPaid ? onPaid() : onClose();
      }
    } catch (e: any) {
      console.error(e);
      const msg = e?.message || e?.response?.data?.message;
      toast.error(msg ? String(msg) : "Falha ao processar pagamento");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Pagamento</DialogTitle>

        <DialogContent>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" color="primary">
              Consulta oftalmológica
            </Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {amountLabel}
            </Typography>
          </Stack>
          {method === "card" && priceDetails && (
            <Typography variant="caption" color="text.secondary">
              Taxa cartão: {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(priceDetails.cardFeeAmount)}
            </Typography>
          )}
          {method === "boleto" && priceDetails && (
            <Typography variant="caption" color="text.secondary">
              Taxa boleto: {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(priceDetails.boletoFeeAmount)}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Resumo
            </Typography>

            <Stack direction="row" spacing={1}>
              <Typography variant="body2">Médico:</Typography>
              <Chip size="small" label={summary.doctor} />
            </Stack>

            {data?.local && (
              <Stack direction="row" spacing={1}>
                <Typography variant="body2">Local:</Typography>
                <Chip size="small" label={summary.local} />
              </Stack>
            )}

            <Typography variant="body2">
              Início:{" "}
              <strong>{formatDateTime(data?.slotStart)}</strong>
            </Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2">Forma de pagamento</Typography>

          <RadioGroup
            row
            value={method}
            onChange={(e) => {
              setMethod(e.target.value as any);
              setPixCopiaCola(null);
              setShowPixDetails(false);
              setOpenPixQr(false);
            }}
          >
            <FormControlLabel value="pix" control={<Radio />} label="Pix" />
            <FormControlLabel
              value="card"
              control={<Radio />}
              label="Cartão de crédito"
            />
            <FormControlLabel
              value="boleto"
              control={<Radio />}
              label="Boleto"
            />
            {canUseTestMode && (
              <FormControlLabel
                value="teste"
                control={<Radio />}
                label="Teste"
              />
            )}
          </RadioGroup>

          <Divider sx={{ my: 2 }} />

          {/* Aviso para modo teste */}
          {//method === "teste" && (
           // <Box sx={{ mb: 2, p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
           //   <Typography variant="body2" color="warning.contrastText">
           //     <strong>Modo Teste:</strong> Este agendamento será criado sem cobrança e já estará confirmado.
           //     Use apenas para testes internos.
           //   </Typography>
           // </Box>
          //)
          }

          {/* Checkbox do contrato - não necessário para modo teste */}
          <FormControlLabel
            control={
              <Checkbox
                checked={contractAccepted}
                onChange={(e) => setContractAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                Li e aceito o{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenContractModal(true);
                  }}
                  sx={{ fontWeight: "bold", cursor: "pointer" }}
                >
                  contrato
                </Link>
              </Typography>
            }
          />
          {!contractAccepted && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Voce precisa aceitar o contrato para prosseguir com o pagamento.
            </Typography>
          )}
            
          

          {method === "pix" && showPixDetails && pixCopiaCola && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {pixExpiresAt && (
                <PixExpirationTimer expiresAt={pixExpiresAt} />
              )}
              <TextField
                label="Pix copia e cola"
                multiline
                minRows={2}
                value={pixCopiaCola}
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={handleCopyPix}>
                  Copiar código
                </Button>
                <Button variant="outlined" onClick={() => setOpenPixQr(true)}>
                  Gerar QRCode
                </Button>
              </Stack>
            </Stack>
          )}

          {method === "card" && contractAccepted && (
            <Stack spacing={2}>
              <Typography variant="caption">Número do cartão</Typography>
              <Box sx={{ border: "1px solid #ccc", p: 1, borderRadius: 1 }}>
                <CardNumberElement
                  options={{
                    ...elementStyle,
                    showIcon: true, 
                  }}
                />
              </Box>

              <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption">Validade</Typography>
                <Box sx={{ border: "1px solid #ccc", p: 1, borderRadius: 1 }}>
                  <CardExpiryElement options={elementStyle} />
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="caption">CVV</Typography>
                <Box sx={{ border: "1px solid #ccc", p: 1, borderRadius: 1 }}>
                  <CardCvcElement options={elementStyle} />
                </Box>
              </Box>
            </Stack>
          </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={processing}>
            Cancelar
          </Button>
          <Button 
            onClick={handlePay} 
            variant="contained" 
            disabled={!data || processing || (method !== "teste" && !contractAccepted)}
          >
            {processing
              ? method === "teste"
                ? "Criando..."
                : method === "pix" || method === "boleto"
                  ? "Gerando..."
                  : "Processando..."
              : method === "teste"
                ? "Criar agendamento de teste"
                : method === "pix"
                  ? "Gerar PIX"
                  : method === "boleto"
                    ? "Solicitar boleto"
                    : `Pagar ${amountLabel}`}
          </Button>
        </DialogActions>
      </Dialog>

      <PixQrModal
        open={openPixQr}
        onClose={() => setOpenPixQr(false)}
        payload={pixCopiaCola ?? ""}
        amountLabel={amountLabel}
        expiresAt={pixExpiresAt}
      />

      <ContractModal
        open={openContractModal}
        onClose={() => setOpenContractModal(false)}
        contractAccepted={contractAccepted}
        onContractAcceptedChange={setContractAccepted}
      />
    </>
  );
}
